import { FormEvent, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpTrayIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  CubeIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { PageShell } from '../../components/layout/page/PageShell';
import {
  portalDashboardMock,
  PortalDashboardFeaturedProject,
  PortalDashboardMilestone,
  PortalDashboardProgressPoint,
  PortalDashboardRequest,
  PortalDashboardSegment,
  PortalDashboardUpdate,
} from './portalDashboardMock';
import { usePortalState } from './PortalState';

const projectIconToneClass: Record<PortalDashboardFeaturedProject['iconTone'], string> = {
  blue: 'bg-primary-50 text-primary-600',
  green: 'bg-emerald-50 text-emerald-600',
  orange: 'bg-orange-50 text-orange-600',
  purple: 'bg-violet-50 text-violet-600',
  sky: 'bg-sky-50 text-sky-600',
};

const projectStatusClass: Record<PortalDashboardFeaturedProject['statusTone'], string> = {
  progress: 'bg-primary-50 text-primary-700',
  analysis: 'bg-amber-50 text-amber-700',
  done: 'bg-emerald-50 text-emerald-700',
};

const milestoneStatusClass: Record<PortalDashboardMilestone['statusTone'], string> = {
  validation: 'bg-amber-50 text-amber-700',
  progress: 'bg-primary-50 text-primary-700',
  planned: 'bg-secondary-100 text-secondary-600',
};

const requestStatusClass: Record<PortalDashboardRequest['statusTone'], string> = {
  approved: 'bg-emerald-50 text-emerald-700',
  progress: 'bg-primary-50 text-primary-700',
  analysis: 'bg-amber-50 text-amber-700',
  answered: 'bg-sky-50 text-sky-700',
};

const updateToneClass: Record<PortalDashboardUpdate['tone'], string> = {
  success: 'bg-emerald-50 text-emerald-600',
  info: 'bg-primary-50 text-primary-600',
  warning: 'bg-amber-50 text-amber-600',
  neutral: 'bg-secondary-100 text-secondary-600',
};

export function PortalHomePage() {
  const { data } = usePortalState();
  const dashboard = portalDashboardMock;
  const firstName = data.clientName.split(' ')[0] || data.clientName;
  const [search, setSearch] = useState('');

  function handleSearch(event: FormEvent) {
    event.preventDefault();
  }

  return (
    <PageShell variant="dashboard">
      <div className="space-y-5">
        <header className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-secondary-900 md:text-3xl">Olá, {firstName}!</h1>
            <p className="mt-1 max-w-2xl text-sm text-secondary-600">
              Aqui está o panorama dos seus projetos. Acompanhe o progresso, entregas e próximas atividades.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <form onSubmit={handleSearch} className="relative min-w-[16rem] flex-1 sm:min-w-[18rem]">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400"
                aria-hidden
              />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={dashboard.searchPlaceholder}
                className="w-full rounded-full border border-secondary-200 bg-white py-2 pl-9 pr-3 text-sm text-secondary-800 placeholder:text-secondary-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </form>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700"
            >
              <CalendarDaysIcon className="h-4 w-4 text-secondary-500" aria-hidden />
              <span>{dashboard.dateRangeLabel}</span>
              <ChevronDownIcon className="h-4 w-4 text-secondary-400" aria-hidden />
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700"
            >
              <span>{dashboard.projectFilterLabel}</span>
              <ChevronDownIcon className="h-4 w-4 text-secondary-400" aria-hidden />
            </button>
          </div>
        </header>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <article className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm text-secondary-500">Projetos ativos</p>
                <p className="mt-2 text-3xl font-bold text-secondary-900">{dashboard.activeProjects.total}</p>
                <p className="mt-2 text-xs font-medium text-emerald-600">{dashboard.activeProjects.deltaLabel}</p>
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                <FolderIcon className="h-5 w-5" aria-hidden />
              </span>
            </div>
          </article>

          <MetricBreakdownCard
            title="Atividades"
            total={dashboard.activities.total}
            icon={<DocumentTextIcon className="h-5 w-5" aria-hidden />}
            iconClassName="bg-emerald-50 text-emerald-600"
            segments={dashboard.activities.segments}
          />
          <MetricBreakdownCard
            title="Entregas"
            total={dashboard.deliveries.total}
            icon={<CubeIcon className="h-5 w-5" aria-hidden />}
            iconClassName="bg-orange-50 text-orange-600"
            segments={dashboard.deliveries.segments}
          />
          <MetricBreakdownCard
            title="Solicitações"
            total={dashboard.requests.total}
            icon={<ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden />}
            iconClassName="bg-violet-50 text-violet-600"
            segments={dashboard.requests.segments}
          />
        </section>

        <section className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,0.85fr)_minmax(0,0.95fr)]">
          <article className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-secondary-900">Progresso geral dos projetos</h2>
              <div className="flex items-center gap-4 text-xs text-secondary-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-0.5 w-4 border-t-2 border-dashed border-primary-300" />
                  Planejado
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-0.5 w-4 bg-primary-500" />
                  Realizado
                </span>
              </div>
            </div>
            <ProgressLineChart
              series={dashboard.progressSeries}
              tooltip={dashboard.progressTooltip}
            />
          </article>

          <article className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold text-secondary-900">Status das atividades</h2>
            <ActivityDonutChart
              total={dashboard.activities.total}
              segments={dashboard.activities.segments}
            />
          </article>

          <article className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-secondary-900">Próximos marcos</h2>
              <Link to="/portal/projects" className="text-sm font-semibold text-primary-700 hover:underline">
                Ver todos
              </Link>
            </div>
            <ul className="mt-4 space-y-3">
              {dashboard.milestones.map((milestone) => (
                <li key={milestone.id} className="flex items-start gap-3">
                  <div className="w-12 shrink-0 text-center">
                    <p className="text-lg font-bold leading-none text-secondary-900">{milestone.day}</p>
                    <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-400">
                      {milestone.month}
                    </p>
                  </div>
                  <div className="min-w-0 flex-1 border-l border-secondary-100 pl-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-secondary-900">{milestone.title}</p>
                        <p className="mt-0.5 truncate text-xs text-secondary-500">{milestone.projectName}</p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${milestoneStatusClass[milestone.statusTone]}`}
                      >
                        {milestone.statusLabel}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </article>
        </section>

        <section className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            <article className="rounded-2xl border border-secondary-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
                <h2 className="text-base font-semibold text-secondary-900">Projetos em destaque</h2>
                <Link to="/portal/projects" className="text-sm font-semibold text-primary-700 hover:underline">
                  Ver todos
                </Link>
              </div>
              <ul className="divide-y divide-secondary-100">
                {dashboard.featuredProjects.map((project) => (
                  <li key={project.id}>
                    <Link
                      to={
                        project.id === 'crm' || project.id === 'app-vendedor'
                          ? `/portal/projects/${project.id}`
                          : '/portal/projects'
                      }
                      className="flex items-center gap-3 px-5 py-3.5 hover:bg-secondary-50"
                    >
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${projectIconToneClass[project.iconTone]}`}
                      >
                        <FolderIcon className="h-5 w-5" aria-hidden />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-secondary-900">{project.name}</p>
                        <p className="truncate text-xs text-secondary-500">{project.summary}</p>
                      </div>
                      <div className="hidden w-28 shrink-0 sm:block">
                        <div className="mb-1 flex items-center justify-between text-xs text-secondary-500">
                          <span>Progresso</span>
                          <span className="font-semibold text-secondary-700">{project.progressPercent}%</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-secondary-100">
                          <div
                            className="h-full rounded-full bg-primary-500"
                            style={{ width: `${project.progressPercent}%` }}
                          />
                        </div>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${projectStatusClass[project.statusTone]}`}
                      >
                        {project.statusLabel}
                      </span>
                      <span className="rounded-lg p-1 text-secondary-400">
                        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </article>

            <article className="rounded-2xl border border-secondary-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
                <h2 className="text-base font-semibold text-secondary-900">Últimas atualizações</h2>
                <button type="button" className="text-sm font-semibold text-primary-700 hover:underline">
                  Ver todas
                </button>
              </div>
              <ul className="divide-y divide-secondary-100 px-5">
                {dashboard.updates.map((update) => (
                  <li key={update.id} className="flex items-start gap-3 py-3.5">
                    <span
                      className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${updateToneClass[update.tone]}`}
                    >
                      <CheckCircleIcon className="h-4 w-4" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-secondary-900">{update.title}</p>
                      <p className="mt-0.5 text-xs text-secondary-500">{update.detail}</p>
                    </div>
                    <span className="shrink-0 text-xs text-secondary-400">{update.whenLabel}</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <div className="space-y-4">
            <article className="rounded-2xl border border-secondary-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
                <h2 className="text-base font-semibold text-secondary-900">Solicitações recentes</h2>
                <Link to="/portal/requests" className="text-sm font-semibold text-primary-700 hover:underline">
                  Ver todas
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="text-xs font-medium text-secondary-500">
                      <th className="px-5 py-3 font-medium">#</th>
                      <th className="px-3 py-3 font-medium">Título</th>
                      <th className="px-3 py-3 font-medium">Projeto</th>
                      <th className="px-3 py-3 font-medium">Tipo</th>
                      <th className="px-3 py-3 font-medium">Status</th>
                      <th className="px-3 py-3 font-medium">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.recentRequests.map((request) => (
                      <tr key={request.id} className="border-t border-secondary-100">
                        <td className="px-5 py-3 font-medium text-secondary-500">{request.number}</td>
                        <td className="max-w-[10rem] truncate px-3 py-3 font-medium text-secondary-900">
                          {request.title}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-secondary-600">{request.projectName}</td>
                        <td className="whitespace-nowrap px-3 py-3 text-secondary-600">{request.typeLabel}</td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${requestStatusClass[request.statusTone]}`}
                          >
                            {request.statusLabel}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-secondary-500">{request.dateLabel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
              <h2 className="text-base font-semibold text-secondary-900">Acesso rápido</h2>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <QuickAction
                  to="/portal/requests"
                  label="Nova solicitação"
                  icon={<PlusIcon className="h-5 w-5" aria-hidden />}
                  tone="primary"
                />
                <QuickAction
                  to="/portal/conversations"
                  label="Iniciar conversa"
                  icon={<ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden />}
                  tone="sky"
                />
                <QuickAction
                  to="/portal/documents"
                  label="Enviar documento"
                  icon={<ArrowUpTrayIcon className="h-5 w-5" aria-hidden />}
                  tone="indigo"
                />
                <QuickAction
                  to="/portal/reports"
                  label="Ver relatórios"
                  icon={<ChartBarIcon className="h-5 w-5" aria-hidden />}
                  tone="violet"
                />
              </div>
            </article>
          </div>
        </section>
      </div>
    </PageShell>
  );
}

function MetricBreakdownCard({
  title,
  total,
  icon,
  iconClassName,
  segments,
}: {
  title: string;
  total: number;
  icon: ReactNode;
  iconClassName: string;
  segments: PortalDashboardSegment[];
}) {
  const sum = segments.reduce((acc, segment) => acc + segment.count, 0) || 1;

  return (
    <article className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-secondary-500">{title}</p>
          <p className="mt-2 text-3xl font-bold text-secondary-900">{total}</p>
          <div className="mt-3 flex h-2 overflow-hidden rounded-full bg-secondary-100">
            {segments.map((segment) => (
              <span
                key={segment.key}
                className="h-full"
                style={{ width: `${(segment.count / sum) * 100}%`, backgroundColor: segment.color }}
              />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-secondary-500">
            {segments.map((segment) => (
              <span key={segment.key} className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: segment.color }} />
                {segment.label}
              </span>
            ))}
          </div>
        </div>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClassName}`}>
          {icon}
        </span>
      </div>
    </article>
  );
}

function ProgressLineChart({
  series,
  tooltip,
}: {
  series: PortalDashboardProgressPoint[];
  tooltip: { monthLabel: string; plannedPercent: number; actualPercent: number };
}) {
  const width = 560;
  const height = 220;
  const padding = { top: 24, right: 16, bottom: 32, left: 36 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const maxY = 100;

  function pointX(index: number): number {
    return padding.left + (index / Math.max(series.length - 1, 1)) * chartWidth;
  }

  function pointY(value: number): number {
    return padding.top + chartHeight - (value / maxY) * chartHeight;
  }

  const plannedPath = series
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${pointX(index)} ${pointY(point.plannedPercent)}`)
    .join(' ');
  const actualPath = series
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${pointX(index)} ${pointY(point.actualPercent)}`)
    .join(' ');
  const areaPath = [
    `M ${pointX(0)} ${pointY(series[0].actualPercent)}`,
    ...series.slice(1).map((point, index) => `L ${pointX(index + 1)} ${pointY(point.actualPercent)}`),
    `L ${pointX(series.length - 1)} ${pointY(0)}`,
    `L ${pointX(0)} ${pointY(0)}`,
    'Z',
  ].join(' ');
  const lastIndex = series.length - 1;

  return (
    <div className="relative mt-4">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-56 w-full" role="img" aria-label="Progresso planejado versus realizado">
        {[0, 25, 50, 75, 100].map((tick) => (
          <g key={tick}>
            <line
              x1={padding.left}
              y1={pointY(tick)}
              x2={width - padding.right}
              y2={pointY(tick)}
              stroke="#E2E8F0"
              strokeWidth="1"
            />
            <text x={padding.left - 8} y={pointY(tick) + 4} textAnchor="end" className="fill-secondary-400 text-[10px]">
              {tick}%
            </text>
          </g>
        ))}
        <path d={areaPath} fill="#1F669D" fillOpacity="0.12" />
        <path d={plannedPath} fill="none" stroke="#7AA8CD" strokeWidth="2" strokeDasharray="6 4" />
        <path d={actualPath} fill="none" stroke="#1F669D" strokeWidth="2.5" />
        {series.map((point, index) => (
          <circle key={point.monthLabel} cx={pointX(index)} cy={pointY(point.actualPercent)} r="3.5" fill="#1F669D" />
        ))}
        <line
          x1={pointX(lastIndex)}
          y1={padding.top}
          x2={pointX(lastIndex)}
          y2={height - padding.bottom}
          stroke="#CBD5E1"
          strokeDasharray="4 4"
        />
        {series.map((point, index) => (
          <text
            key={`label-${point.monthLabel}`}
            x={pointX(index)}
            y={height - 10}
            textAnchor="middle"
            className="fill-secondary-500 text-[11px]"
          >
            {point.monthLabel}
          </text>
        ))}
      </svg>
      <div
        className="pointer-events-none absolute rounded-xl border border-secondary-200 bg-white px-3 py-2 text-xs shadow-md"
        style={{ right: '8%', top: '18%' }}
      >
        <p className="font-semibold text-secondary-900">{tooltip.monthLabel}</p>
        <p className="mt-1 text-secondary-600">Planejado: {tooltip.plannedPercent}%</p>
        <p className="text-primary-700">Realizado: {tooltip.actualPercent}%</p>
      </div>
    </div>
  );
}

function ActivityDonutChart({
  total,
  segments,
}: {
  total: number;
  segments: PortalDashboardSegment[];
}) {
  const radius = 58;
  const stroke = 16;
  const circumference = 2 * Math.PI * radius;
  const sum = segments.reduce((acc, segment) => acc + segment.count, 0) || 1;
  let offset = 0;

  return (
    <div className="mt-4 flex flex-col items-center gap-4">
      <div className="relative h-40 w-40">
        <svg viewBox="0 0 140 140" className="h-40 w-40 -rotate-90" aria-hidden>
          <circle cx="70" cy="70" r={radius} fill="none" stroke="#F1F5F9" strokeWidth={stroke} />
          {segments.map((segment) => {
            const length = (segment.count / sum) * circumference;
            const circle = (
              <circle
                key={segment.key}
                cx="70"
                cy="70"
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={stroke}
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
              />
            );
            offset += length;
            return circle;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-2xl font-bold text-secondary-900">{total}</p>
          <p className="text-xs text-secondary-500">Total</p>
        </div>
      </div>
      <ul className="w-full space-y-2">
        {segments.map((segment) => (
          <li key={segment.key} className="flex items-center justify-between text-sm">
            <span className="inline-flex items-center gap-2 text-secondary-600">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: segment.color }} />
              {segment.label.replace(/^\d+\s/, '')}
            </span>
            <span className="font-semibold text-secondary-800">
              {Math.round((segment.count / sum) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuickAction({
  to,
  label,
  icon,
  tone,
}: {
  to: string;
  label: string;
  icon: ReactNode;
  tone: 'primary' | 'sky' | 'indigo' | 'violet';
}) {
  const toneClass = {
    primary: 'bg-primary-50 text-primary-700 hover:bg-primary-100',
    sky: 'bg-sky-50 text-sky-700 hover:bg-sky-100',
    indigo: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
    violet: 'bg-violet-50 text-violet-700 hover:bg-violet-100',
  }[tone];

  return (
    <Link
      to={to}
      className={`flex min-h-[5.5rem] flex-col items-center justify-center gap-2 rounded-2xl border border-secondary-100 px-3 py-4 text-center text-sm font-semibold transition-colors ${toneClass}`}
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}
